import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { toAbsoluteUrl } from "_metronic/_helpers";
import Avatar from "app/popleads/components/ui-kit/Avatar";
import Button from "app/popleads/components/ui-kit/Button";
import { BodyStrong, Caption } from "app/popleads/components/ui-kit/Text";
import { approvalRenderer } from "./assistantApprovals";
import AttachmentChip from "./AttachmentChip";
import AttachmentImage from "./AttachmentImage";
import { sessionImageUrl } from "./assistantSessionImages";
import { answerToPlainText, writeAnswerToClipboard } from "./assistantClipboard";
import { ANSWER_URI_REGEXP, isFollowableLink } from "./assistantLinks";
import { ASSISTANT_ORB } from "./assistantIcons";
import { assistantDisplayName } from "./assistantIdentity";

import styles from "./AssistantChatItem.module.scss";
import moment from "moment";

/**
 * An answer arrives as markdown from the model and reaches the page as HTML, so it goes through
 * two steps and neither is optional.
 *
 * `marked` renders it, because without that step `**Task:**` shows up on screen with its asterisks
 * — the component used to inject the string straight into innerHTML, which was right when the old
 * backend returned HTML and wrong the moment a model started writing the answers.
 *
 * `DOMPurify` then sanitises it, because the text is not the model's own words: it carries CRM
 * field values the MCP tools fetched — lead names, task descriptions, notes. A record containing
 * `<img onerror=...>` would otherwise run in this page with the signed-in user's session. The
 * pipeline reaches arbitrary CRM free text, so the answer is untrusted input, model or not.
 */
const answerRenderer = new marked.Renderer();
const renderLink = answerRenderer.link.bind(answerRenderer);
/* A link that cannot be followed as written is left as its words — see isFollowableLink. */
answerRenderer.link = (href, title, text) => (isFollowableLink(href) ? renderLink(href, title, text) : text);

const renderAnswer = (markdown) =>
    DOMPurify.sanitize(
        marked.parse(typeof markdown === "string" ? markdown : "", { breaks: true, renderer: answerRenderer }),
        { ALLOWED_URI_REGEXP: ANSWER_URI_REGEXP }
    );

/**
 * Copies the answer as it reads on screen, never the markdown behind it: `**Imperum**` pasted into
 * an email is the bug this replaces. See writeAnswerToClipboard for the two formats. The tick stays
 * for two seconds, the way ChatGPT's does.
 */
const CopyAnswer = ({ markdown, html }) => {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return undefined;
        const timer = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(timer);
    }, [copied]);

    const copy = async () => {
        try {
            await writeAnswerToClipboard({ html, text: answerToPlainText(markdown) });
            setCopied(true);
        } catch (e) {
            toast("The answer could not be copied.", { type: "error" });
        }
    };

    return (
        <Button
            label={copied ? "Copied" : "Copy answer"}
            tooltip={copied ? "Copied" : "Copy"}
            icon={copied ? "check" : "copy"}
            variant="neutral"
            buttonStyle="borderless"
            size="xsmall"
            onClick={copy}
        />
    );
};

/**
 * Inside a palette an exchange reads as a report, not as a chat: the question is the quiet line
 * that says what was run, and the answer is the full-width block underneath it. Two bubbles
 * facing each other would waste the width the palette exists to give.
 *
 * `plain` is the CRM window's reading, the way ChatGPT draws a thread: no box round an answer, no
 * name, mark or time on it, and a row of actions under it instead. The extension does not pass it
 * and keeps its cards, each headed by the assistant's name and the time.
 */
/* One line naming the error a question was about: the request and its status, or the screen. */
export const errorSummary = (errorContext) => {
    const context = errorContext?.context || {};
    if (context.request) return [context.request.fn, context.request.status].filter(Boolean).join(" · ");
    if (context.crash) return "Screen error";
    return "Error";
};

/**
 * What went to the assistant with a question asked from an error's "Ask the assistant" button —
 * shown because the button sends it without asking, and the person should be able to see exactly
 * what left the browser. The server's own record of the request is not here: it never comes to the
 * browser.
 */
const ErrorContextDetails = ({ errorContext }) => (
    <details className={styles.errorContext}>
        <summary>Error details sent · {errorSummary(errorContext)}</summary>
        <pre>{JSON.stringify(errorContext.context, null, 2)}</pre>
    </details>
);

const ChatItem = ({
    plain = false,
    showIdentity = true,
    role,
    content,
    timestamp,
    attachments,
    errorContext,
    approval,
    result,
    isApprovalBusy,
    onApprovalDecide,
    onApprovalNote,
    onApprovalBlock,
    onApprovalFail,
}) => {
    const { user } = useSelector((state) => state.auth);
    const answerHtml = useMemo(() => (role === "user" ? "" : renderAnswer(content)), [role, content]);
    /* Which card the request draws as is the approval's own business, not this component's —
       see assistantApprovals. The props below are the same for every one of them. */
    const ApprovalCard = approval ? approvalRenderer(approval) : null;

    /* The question sits on the right with no label. Which side it is on already says who asked it,
       and the name was repeated on every turn above an answer block that names the assistant. */
    if (role === "user") {
        return (
            <div className={`${styles.question} ${plain ? styles.plain : ""}`}>
                {/* Text, not innerHTML: this is what the user typed, so there is nothing to render
                    and everything to escape. An attachment can be the whole message, so this is
                    skipped rather than left as an empty line when there is no caption. */}
                {content && <div className={styles.questionText}>{content}</div>}
                {errorContext && <ErrorContextDetails errorContext={errorContext} />}
                {attachments?.length > 0 && (
                    <div className={styles.questionAttachments}>
                        {attachments.map((attachment) => {
                            const src = sessionImageUrl(attachment.imageId);
                            return src ? (
                                <AttachmentImage key={attachment.name} name={attachment.name} src={src} />
                            ) : (
                                <AttachmentChip key={attachment.name} name={attachment.name} />
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    return (
        <article className={`${styles.answer} ${plain ? styles.plain : ""}`}>
            {showIdentity && !plain && (
                <header className={styles.answerHead}>
                    <Avatar src={toAbsoluteUrl(ASSISTANT_ORB)} size={22} radius={11} />
                    <BodyStrong className={styles.answerName}>{assistantDisplayName(user)}</BodyStrong>
                    <Caption className={styles.answerTime}>{moment(timestamp).format("HH:mm")}</Caption>
                </header>
            )}
            {/* A turn that stops for permission usually has nothing to say yet, and an empty body
                would still take the block's line-height above the card. */}
            {answerHtml && (
                <div dangerouslySetInnerHTML={{ __html: answerHtml }} className={`${styles.answerBody} text-card`} />
            )}
            {/* A turn that stopped for permission carries the request instead of an answer — the
                model has not run the tool yet and is waiting on this. The card stays after it is
                answered, so this renders in both states; the component knows which one it is in. */}
            {/* `onNote` is how a card says something to the thread on its own account, rather than
                answering the request it was drawn for — the campaign card uses it to report an
                undo, which happens after the decision and never goes near the model. */}
            {/* `onBlock` is the card saying the proposal cannot go ahead at all. The thread then puts
                the reason in this message's place and marks the approval `blocked`, and a blocked
                approval draws no card — the reason is the answer. `onFail` is the write being
                refused after Confirm: the card stays, closed, and the thread adds a message saying
                it was tried and did not go through. */}
            {approval && approval.decision !== "blocked" && (
                <ApprovalCard
                    approval={approval}
                    result={result}
                    isBusy={isApprovalBusy}
                    onDecide={onApprovalDecide}
                    onNote={onApprovalNote}
                    onBlock={onApprovalBlock}
                    onFail={onApprovalFail}
                />
            )}
            {plain && answerHtml && (
                <div className={styles.answerActions}>
                    <CopyAnswer markdown={content} html={answerHtml} />
                </div>
            )}
        </article>
    );
};

export default ChatItem;
